<script setup>
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';

import Icon from './ds/Icon.vue';
import OteraMark from './ds/OteraMark.vue';
import SkipToContent from './ds/SkipToContent.vue';
import TenantPicker from './ds/TenantPicker.vue';

import { useTheme } from '@/composables/useTheme.js';
import { useAuthStore } from '@/stores/auth.js';
import { useTenantStore } from '@/stores/tenant.js';

const emit = defineEmits(['logout']);

const auth = useAuthStore();
const tenant = useTenantStore();
const { user, isAdmin, isOperator, isMultiTenant, tenants } = storeToRefs(auth);
const { theme, toggleTheme } = useTheme();

const NAV_ADMIN = [
  { to: '/admin/health', label: 'Health' },
  { to: '/admin/tenants', label: 'Clients' },
  { to: '/admin/apps', label: 'Apps' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/audit', label: 'Audit log' },
  // Vista tal cual la ve el cliente (T001-3). Sale del shell a
  // propósito: sirve para verificar qué se está publicando.
  { to: '/portal', label: 'Client portal' }
];
const NAV_INTERNAL = [{ to: '/probes', label: 'Probes' }];
const navLinks = computed(() => [...NAV_INTERNAL, ...(isAdmin.value ? NAV_ADMIN : [])]);

const truncatedEmail = computed(() => {
  const email = user.value?.email;
  if (!email) return '';
  return email.length > 28 ? `${email.slice(0, 25)}…` : email;
});

const avatarInitial = computed(() =>
  (user.value?.email || '?').charAt(0).toUpperCase()
);

const themeLabel = computed(() =>
  theme.value === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'
);

function onTenantSelect(appId) {
  tenant.setActiveTenant(appId);
}

// --- Menú de usuario (dropdown) ---
const menuOpen = ref(false);
const menuRef = ref(null);

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

function closeMenu() {
  menuOpen.value = false;
}

function onLogout() {
  closeMenu();
  emit('logout');
}

function onDocClick(e) {
  if (menuOpen.value && menuRef.value && !menuRef.value.contains(e.target)) {
    closeMenu();
  }
}

function onDocKeydown(e) {
  if (e.key === 'Escape') closeMenu();
}

onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onDocKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onDocKeydown);
});
</script>

<template>
  <div class="shell">
    <SkipToContent target="main" />

    <!-- Story 1.18 AC5 calls for explicit role="banner" on header. -->
    <!-- eslint-disable-next-line vuejs-accessibility/no-redundant-roles -->
    <header
      class="tb"
      role="banner"
    >
      <div class="tb__left">
        <RouterLink
          to="/"
          class="tb__brand"
        >
          <OteraMark />
          Otera
        </RouterLink>

        <nav
          v-if="isAdmin || isOperator"
          class="tb__nav"
          aria-label="Main"
        >
          <RouterLink
            v-for="link in navLinks"
            :key="link.to"
            :to="link.to"
            class="tb__nav-item"
            active-class="is-active"
          >
            {{ link.label }}
          </RouterLink>
        </nav>
      </div>

      <div class="tb__right">
        <slot name="time-window" />

        <button
          type="button"
          class="tb__icon-btn"
          :aria-label="themeLabel"
          :title="themeLabel"
          @click="toggleTheme"
        >
          <Icon
            :name="theme === 'dark' ? 'sun' : 'moon'"
            :size="15"
          />
        </button>

        <TenantPicker
          v-if="isMultiTenant"
          :tenants="tenants"
          :active-app-id="tenant.activeAppId"
          @select="onTenantSelect"
        />

        <div
          v-if="user"
          ref="menuRef"
          class="shell__menu"
        >
          <button
            type="button"
            class="tb__user shell__user-btn"
            :title="user.email"
            aria-haspopup="menu"
            :aria-expanded="menuOpen"
            data-test="user-menu-toggle"
            @click="toggleMenu"
          >
            <span
              class="tb__avatar"
              aria-hidden="true"
            >{{ avatarInitial }}</span>
            <span class="tb__user-name">{{ truncatedEmail }}</span>
            <Icon
              name="chevDown"
              :size="14"
            />
          </button>

          <div
            v-if="menuOpen"
            class="shell__dropdown"
            role="menu"
            data-test="user-menu"
          >
            <RouterLink
              to="/profile"
              class="shell__dropdown-item"
              role="menuitem"
              data-test="user-menu-profile"
              @click="closeMenu"
            >
              <Icon
                name="user"
                :size="15"
              />
              Profile
            </RouterLink>
            <button
              type="button"
              class="shell__dropdown-item"
              role="menuitem"
              data-test="user-menu-logout"
              @click="onLogout"
            >
              <Icon
                name="logout"
                :size="15"
              />
              Log out
            </button>
          </div>
        </div>
      </div>
    </header>

    <main
      id="main"
      class="shell__main"
    >
      <slot />
    </main>
  </div>
</template>

<style scoped>
.shell {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-subtle);
}

/* Menú de usuario — el toggle reusa el estilo de .tb__user, el dropdown
   se posiciona absoluto bajo el header. */
.shell__menu {
  position: relative;
}

.shell__user-btn {
  border: 0;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

.shell__dropdown {
  position: absolute;
  top: calc(100% + var(--space-2));
  right: 0;
  z-index: 50;
  min-width: 11rem;
  display: flex;
  flex-direction: column;
  padding: var(--space-1);
  background: var(--bg-canvas);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
}

.shell__dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 0;
  background: transparent;
  border-radius: var(--radius-sm);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--fg-primary);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.shell__dropdown-item:hover {
  background: var(--bg-subtle);
}

.shell__main {
  flex: 1;
  padding: var(--space-7) var(--space-9);

  /* tabindex='-1' lo aplica SkipToContent en runtime para que el foco
     pueda aterrizar aquí. */
}
</style>
